import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Checkbox, Typography, Divider, Breadcrumb, Form, Button, message, Skeleton as SkeletonAntd } from 'antd';

import Wrapper from '@/components/shared/Wrapper';
import { NotificatioForm } from '@/interfaces/notification';
import { useGetNotificationConfigQuery, useSaveNotificationConfigMutation } from '@/services/notifications';
import styled, { css, cx, StyledProp } from '@/styled';

import { itemMapper } from './notificationMapper';
import UnsubscribeList from './UnsubscribeList';

const initialValue: Record<string, { email: boolean; push: boolean }> = {
  newContent: { email: true, push: true },
  newCourseTrack: { email: true, push: true },
  comment: { email: true, push: true },
  sendEmailToStudents: { email: true, push: true }
};

const Notifications = ({ className }: StyledProp) => {
  const config = useGetNotificationConfigQuery();
  const [saveConfig] = useSaveNotificationConfigMutation();
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [previousConfig, setPreviousConfig] = useState<NotificatioForm>(null);
  const navigate = useNavigate();
  const [form] = Form.useForm<NotificatioForm>();

  const submit = (form: NotificatioForm) => {
    const data: any = { ...form };
    setPreviousConfig(data);
    saveConfig(data)
      .then(() => message.success('Configurações salvas com sucesso!'))
      .catch(() => message.error('Ocorreu um erro ao salvar as configurações!'));
  };

  const toggle = () => {
    setIsAllSelected(prev => !prev);
    const newValues = Object.assign(initialValue, {});
    Object.keys(initialValue).forEach(key => {
      newValues[key] = { email: !isAllSelected, push: !isAllSelected };
    });

    form.setFieldsValue(newValues);
  };

  const reset = () => {
    form.setFieldsValue(previousConfig);
  };

  const crumbs = [
    {
      title: 'Resumo',
      onClick: () => navigate('/resumo')
    },
    {
      title: 'Notificações'
    }
  ];

  const checkIsAllSelected = () => {
    const values = form.getFieldsValue() as NotificatioForm;
    setIsAllSelected(Object.values(values).every(e => e.email && e.push));
  };

  useEffect(() => {
    if (config.data) {
      setPreviousConfig(config.data);
      form.setFieldsValue(config.data);
      setIsAllSelected(Object.values(config.data).every(e => e.email && e.push));
    }
  }, [config, form]);

  const Skeleton = () => (
    <div className={cx(className, 'notification-skeleton')}>
      <SkeletonAntd.Button block size='large' />
      <SkeletonAntd.Button block size='large' />
      <SkeletonAntd.Button block size='large' />
      <SkeletonAntd.Button block size='large' />
    </div>
  );

  return (
    <Wrapper className={className} size='medium'>
      <Breadcrumb items={crumbs} />
      <Typography.Title level={1}>Notificações</Typography.Title>
      {config.isFetching ? (
        <Skeleton />
      ) : (
        <Form form={form} onFinish={submit} onReset={reset}>
          <Form.Item>
            <Checkbox className='checkbox' checked={isAllSelected} onClick={toggle}>
              Ativar todos
            </Checkbox>
          </Form.Item>
          <div className='notifications-items header'>
            <div />
            <Typography.Text strong>E-mail</Typography.Text>
            <Typography.Text strong>Push</Typography.Text>
          </div>
          <Divider type='horizontal' className='notifications-divider' />
          {itemMapper.map((item, index) => (
            <div key={index}>
              <div className='notifications-items'>
                <div className='notifications-itemDescription' style={{ gridArea: 'description' }}>
                  <Typography.Text strong>{item.title}</Typography.Text>
                  <Typography.Text className='notifications-itemDescriptionsText'>{item.description}</Typography.Text>
                </div>
                <Form.Item
                  name={[item.key, 'email']}
                  rootClassName='checkbox'
                  valuePropName='checked'
                  style={{ gridArea: 'email' }}
                >
                  <Checkbox onChange={checkIsAllSelected}>E-mail</Checkbox>
                </Form.Item>
                {!item.disablePush && (
                  <Form.Item
                    name={[item.key, 'push']}
                    rootClassName='checkbox'
                    valuePropName='checked'
                    style={{ gridArea: 'push' }}
                  >
                    <Checkbox onChange={checkIsAllSelected}>Push</Checkbox>
                  </Form.Item>
                )}
              </div>
              <Divider className='notifications-divider' />
            </div>
          ))}
          <div className='notifications-footer'>
            <Form.Item>
              <Button type='primary' size='large' htmlType='submit'>
                Salvar
              </Button>
            </Form.Item>
            <Form.Item>
              <Button className='notifications-btnReset' type='ghost' htmlType='reset'>
                Descartar Alterações
              </Button>
            </Form.Item>
          </div>
        </Form>
      )}
      <UnsubscribeList />
    </Wrapper>
  );
};

export default styled(Notifications)(
  ({ theme }) => css`
    h1 {
      margin-top: ${theme.space.pxToRem(24)};
      margin-bottom: ${theme.space.pxToRem(48)};
    }

    .notification-skeleton {
      gap: ${theme.space.pxToRem(24)};
      display: flex;
      margin: ${theme.space.pxToRem(130)} 0;
      flex-direction: column;

      .ant-skeleton-button {
        height: 64px;
      }
    }

    .notifications-footer {
      display: flex;
      flex-direction: row-reverse;
      margin: ${theme.space.spacing(6, 0)};
      column-gap: ${theme.space.spacing(8)};
      row-gap: ${theme.space.spacing(1)};
      align-items: center;

      .ant-form-item {
        margin: 0;
      }

      ${theme.breakpoints.down('md')} {
        flex-direction: column;

        .ant-form-item,
        button {
          width: 100%;

          span {
            width: 100%;
            text-align: center;
          }
        }
      }
    }

    .notifications-btnReset {
      color: ${theme.colorTextBase};
    }

    .label {
      width: 48px;
      color: ${theme.palette.gray[0]};
    }

    .header {
      ${theme.breakpoints.down('md')} {
        display: none !important;
      }
    }

    .notifications-items {
      display: grid;
      grid-template-columns: auto 64px 64px;
      justify-items: flex-end;
      align-items: center;
      padding: ${theme.space.pxToRem(16)} ${theme.space.pxToRem(4)};
      grid-template-areas: 'description email push';
      grid-row-gap: ${theme.space.pxToRem(12)};

      ${theme.breakpoints.up('md')} {
        label > span:last-child {
          display: none;
        }
      }

      ${theme.breakpoints.down('md')} {
        grid-template-columns: 90px;
        justify-items: flex-start;
        grid-template-areas:
          'description description'
          'email push';
      }
    }

    .notifications-itemDescription {
      display: flex;
      width: 100%;
      flex-direction: column;
    }

    .ant-checkbox-wrapper {
      margin-inline-start: 0px;
    }

    .ant-checkbox-wrapper {
      height: 20px;
      span {
        line-height: 1.9;
        color: ${theme.colorText};
      }
    }

    .ant-checkbox,
    .ant-checkbox-input,
    .ant-checkbox-inner,
    .checkbox .ant-form-item-control-input,
    .checkbox .ant-checkbox-wrapper {
      min-width: 20px;
      height: 20px;
    }

    .checkbox,
    .checkbox .ant-form-item-control-input {
      margin: 0;
      min-width: 20px;
      min-height: 20px;
    }

    .notifications-itemDescriptionsText {
      color: ${theme.colorTextTertiary};
    }

    .notifications-divider {
      margin: 0;
      border-color: ${theme.variables.borderColor};

      ${theme.breakpoints.down('md')} {
        margin: ${theme.space.spacing(2, 0)};
      }
    }
  `
);
